import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, MessageSquarePlus, Send, Briefcase, Loader2, X, Crown, Users } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";
import { useChatUploads, useChatDropPaste, AttachButton, PendingAttachments, AttachmentList, DragOverlay } from "@/components/chat/Attachments";

const fmtTime = (iso) =>
  new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const CONVOS = [
  {
    id: "pm_team",
    label: "Production Manager Team",
    short: "PM Team",
    icon: Users,
    blurb: "Shared production support — any Production Manager can jump in and reply.",
  },
  {
    id: "owner",
    label: "Melto (Owner)",
    short: "Owner",
    icon: Crown,
    blurb: "Private line to the owner. Only you and Melto can read this.",
  },
];

// Human label for a staff message inside a thread — the shared PM Team conversation
// always shows WHO actually wrote the message, never a faceless "team" account.
const staffLabel = (m) => {
  const name = m.sender_name || "Melto";
  if (m.sender_role === "manager") return `${name} — Production Manager`;
  if (m.sender_role === "admin") return `${name} — Owner`;
  return m.sender_name ? name : "Melto";
};

export const WorkerMessages = ({ jobs, onRead, preset, onPresetConsumed }) => {
  const [conv, setConv] = useState("pm_team");
  const [messages, setMessages] = useState(null);
  const [unread, setUnread] = useState({ owner: 0, pm_team: 0 });
  const [text, setText] = useState("");
  const [jobId, setJobId] = useState("");
  const [sending, setSending] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const bottomRef = useRef(null);
  const uploads = useChatUploads();
  const { dragging, dragProps, onPaste } = useChatDropPaste(uploads.addFiles);

  const load = useCallback(
    (silent, c = conv) => {
      axios
        .get(`${API}/my/messages?conversation=${c}`, { headers: authHeaders() })
        .then((r) => {
          setMessages(r.data);
          onRead?.();
        })
        .catch(() => {
          if (!silent) toast.error("Could not load messages.");
        });
      axios.get(`${API}/my/messages/unread`, { headers: authHeaders() }).then((r) => setUnread(r.data)).catch(() => {});
    },
    [conv, onRead]
  );

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 15000);
    return () => clearInterval(t);
  }, [load]);

  // "Ask a question" on a job lands here: open the shared PM Team thread with the job pre-linked
  useEffect(() => {
    if (!preset) return;
    setConv(preset.conversation || "pm_team");
    if (preset.jobId) setJobId(preset.jobId);
    onPresetConsumed?.();
  }, [preset, onPresetConsumed]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages?.length, conv]);

  const send = async (e) => {
    e.preventDefault();
    if ((!text.trim() && uploads.metas.length === 0) || uploads.uploading || uploads.failed) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, { text: text.trim(), job_id: jobId || null, conversation: conv, attachments: uploads.metas }, { headers: authHeaders() });
      setText("");
      setJobId("");
      uploads.clear();
      load(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Message failed to send.");
    } finally {
      setSending(false);
    }
  };

  const switchConv = (c) => {
    setConv(c);
    setMessages(null);
    setJobId("");
  };

  const linkableJobs = (jobs || []).filter((j) => ["active", "awaiting_review", "in_revision"].includes(j.status));
  const linkedJob = linkableJobs.find((j) => j.id === jobId);
  const active = CONVOS.find((c) => c.id === conv);

  return (
    <div data-testid="worker-messages" {...dragProps} className="relative flex h-[65vh] flex-col rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)]">
      <DragOverlay visible={dragging} />
      <div className="flex items-center justify-between gap-2.5 border-b border-[var(--pv-border)] px-6 py-4">
        <div className="flex items-center gap-2.5">
          <MessageSquare className="h-4 w-4 text-cyan-400" />
          <div>
            <p className="font-display text-sm font-bold text-[var(--pv-text)]">Messages</p>
            <p className="text-[11px] text-[var(--pv-text-4)]">{active.blurb}</p>
          </div>
        </div>
        <button
          data-testid="new-chat-btn"
          onClick={() => setShowNewChat(true)}
          className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-3.5 py-2 text-[11px] font-bold text-white shadow-md shadow-cyan-500/25 transition-colors hover:bg-cyan-600"
        >
          <MessageSquarePlus className="h-3.5 w-3.5" /> New chat
        </button>
      </div>

      <div className="flex gap-2 border-b border-[var(--pv-border)] px-4 py-2.5">
        {CONVOS.map((c) => (
          <button
            key={c.id}
            data-testid={`convo-tab-${c.id}`}
            onClick={() => switchConv(c.id)}
            className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${conv === c.id ? "bg-cyan-500 text-white" : "border border-[var(--pv-border)] text-[var(--pv-text-3)] hover:bg-[var(--pv-subtle)]"}`}
          >
            <c.icon className="h-3.5 w-3.5" /> {c.short}
            {(unread[c.id] || 0) > 0 && (
              <span data-testid={`convo-unread-${c.id}`} className={`flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold ${conv === c.id ? "bg-white text-cyan-600" : "bg-rose-500 text-white"}`}>
                {unread[c.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4 sm:px-6">
        {!messages ? (
          <p data-testid="messages-loading" className="py-16 text-center text-sm font-medium text-[var(--pv-text-4)]">Loading messages...</p>
        ) : messages.length === 0 ? (
          <div data-testid="messages-empty" className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <active.icon className="h-10 w-10 text-[var(--pv-text-4)]" />
            <p className="font-display text-base font-bold text-[var(--pv-text)]">No messages yet</p>
            <p className="max-w-sm px-6 text-sm text-[var(--pv-text-4)]">
              {conv === "pm_team"
                ? "Ask the production team anything — link the job so it's clear what you mean."
                : "Say hi to Melto, or ask something privately — link a job if it's about one."}
            </p>
          </div>
        ) : (
          messages.map((m) => (
            <div key={m.id} data-testid={`message-${m.id}`} className={`flex ${m.sender === "worker" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${m.sender === "worker" ? "bg-cyan-500 text-white" : "bg-[var(--pv-raised)] text-[var(--pv-text)]"}`}>
                {m.sender !== "worker" && conv === "pm_team" && (
                  <p data-testid={`message-sender-${m.id}`} className="mb-0.5 text-[10px] font-bold uppercase tracking-wide text-fuchsia-300">{staffLabel(m)}</p>
                )}
                {m.job_title && (
                  <p className={`mb-1 flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold ${m.sender === "worker" ? "bg-white/20 text-white" : "bg-cyan-500/15 text-cyan-300"}`}>
                    <Briefcase className="h-3 w-3" /> Question about: {m.job_title}
                  </p>
                )}
                <AttachmentList attachments={m.attachments} mine={m.sender === "worker"} />
                {m.text ? <p className="whitespace-pre-wrap text-sm leading-relaxed">{m.text}</p> : null}
                <p className={`mt-1 text-right text-[10px] ${m.sender === "worker" ? "text-cyan-100" : "text-[var(--pv-text-4)]"}`}>
                  {m.sender !== "worker" && conv === "owner" ? `${staffLabel(m)} · ` : ""}{fmtTime(m.created_at)}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      <form data-testid="worker-message-form" onSubmit={send} className="border-t border-[var(--pv-border)] p-4">
        <PendingAttachments pending={uploads.pending} onRetry={uploads.retry} onRemove={uploads.remove} />
        {linkedJob && (
          <div className="mb-2 flex items-center gap-2">
            <span data-testid="linked-job-chip" className="flex items-center gap-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/15 px-3 py-1 text-[11px] font-bold text-cyan-300">
              <Briefcase className="h-3 w-3" /> Question about: {linkedJob.title}
              <button type="button" data-testid="unlink-job-btn" onClick={() => setJobId("")} className="text-cyan-300 hover:text-white" aria-label="Remove job link">
                <X className="h-3 w-3" />
              </button>
            </span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <select
            data-testid="message-job-select"
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
            className="w-44 shrink-0 rounded-full border border-[var(--pv-border)] bg-[var(--pv-bg)] px-3 py-2.5 text-xs font-semibold text-[var(--pv-text-3)] focus:border-cyan-400 focus:outline-none"
          >
            <option value="">No job linked</option>
            {linkableJobs.map((j) => (
              <option key={j.id} value={j.id}>{j.title}</option>
            ))}
          </select>
          <input
            data-testid="message-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onPaste={onPaste}
            placeholder={conv === "pm_team" ? "Message the production team..." : "Message Melto..."}
            maxLength={2000}
            className="min-w-0 flex-1 rounded-full border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10"
          />
          <AttachButton onFiles={uploads.addFiles} disabled={sending} />
          <button
            data-testid="message-send-btn"
            type="submit"
            disabled={sending || uploads.uploading || uploads.failed || (!text.trim() && uploads.metas.length === 0)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cyan-500 text-white shadow-md shadow-cyan-500/25 transition-colors hover:bg-cyan-600 disabled:opacity-50"
            aria-label="Send message"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </form>

      {showNewChat && (
        <div data-testid="new-chat-picker" className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => setShowNewChat(false)}>
          <div className="w-full max-w-md overflow-hidden rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[var(--pv-border)] px-5 py-4">
              <div>
                <p className="font-display text-sm font-bold text-[var(--pv-text)]">Start a conversation</p>
                <p className="text-[11px] text-[var(--pv-text-4)]">Existing conversations pick up right where you left off.</p>
              </div>
              <button data-testid="new-chat-close" onClick={() => setShowNewChat(false)} className="rounded-full p-1.5 text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)]" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3 p-5">
              {CONVOS.map((c) => (
                <button
                  key={c.id}
                  data-testid={`new-chat-${c.id}`}
                  onClick={() => { setShowNewChat(false); switchConv(c.id); }}
                  className="flex w-full items-start gap-3 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-raised)] p-4 text-left transition-all hover:-translate-y-0.5 hover:border-cyan-500/40"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cyan-500/15 text-cyan-300">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-sm font-bold text-[var(--pv-text)]">{c.label}</span>
                    <span className="mt-0.5 block text-xs text-[var(--pv-text-4)]">{c.blurb}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
